import { BaseService } from "./base.service";
import { BaseResponse, BaseResponseExternal } from "./dtos/base.dto";
import {
  BillsResponse,
  DaftarZiswafResponse,
  PaymentSavingRequest,
  PocketResponse,
  VirtualAccountReportResponse,
  VirtualAccountRequest,
  VirtualAccountRequestZiswaf,
} from "./dtos/finance.dto";

interface FinanceServiceInterface {
  queries: {
    GET_FINANCE_BILLS_PAID_OF: string;
    GET_FINANCE_ZIS: string;
    GET_FINANCE_POCKET: string;
  };
  billsPaidOf(payload: { nis: string }): Promise<BillsResponse>;
  virtualAccountRequest(payload: VirtualAccountRequest): Promise<BaseResponse>;
  virtualAccountReport(payload: {
    nis: string;
    limit: string;
  }): Promise<VirtualAccountReportResponse>;
  virtualAccountCheckStatus(payload: { trxID: string }): Promise<BaseResponse>;
  virtualAccountCancel(id: string): Promise<BaseResponse>;
  virtualAccountConfirm(id: string): Promise<BaseResponse>;
  virtualAccountUploadBukti(payload: { trx_id: string; bukti_transaksi: File }): Promise<BaseResponse>;
  ziswafList(): Promise<DaftarZiswafResponse>;
  virtualAccountRequestZiswaf(
    payload: VirtualAccountRequestZiswaf,
  ): Promise<BaseResponse>;
  paymentSavingRequest(payload: PaymentSavingRequest): Promise<BaseResponse>;
  pocket(): Promise<PocketResponse>;
}

export const FinanceService: FinanceServiceInterface = {
  queries: {
    GET_FINANCE_BILLS_PAID_OF: "GET_FINANCE_BILLS_PAID_OF",
    GET_FINANCE_ZIS: "GET_FINANCE_ZIS",
    GET_FINANCE_POCKET: "GET_FINANCE_POCKET",
  },

  billsPaidOf: async (payload: { nis: string }) => {
    try {
      const request = await BaseService.ci4Request().get<BillsResponse>(
        "/tagihan/data",
        { params: payload },
      );
      return {
        success: request.data.success,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },

  ziswafList: async () => {
    try {
      const request = await BaseService.ci4Request().get(
        "/tagihan/daftar-ziswaf",
      );
      return {
        success: request.data.success,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },

  virtualAccountRequest: async (payload: VirtualAccountRequest) => {
    try {
      const request = await BaseService.ci4Request().post<
        BaseResponseExternal<{
          trxAmount: number;
          nis: string;
          customerName: string;
          virtualAccount: string;
          datetimeExpired: Date;
          trxID: string;
          jenis: string;
          requestPayment: string;
        }>
      >("/tagihan/submit-va-tagihan", payload);
      return {
        success: request.data.status,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },

  virtualAccountReport: async (payload: { nis: string; limit: string }) => {
    try {
      const request = await BaseService.ci4Request().get<VirtualAccountReportResponse>(
        "/tagihan/data-va",
        { params: payload },
      );
      return {
        success: request.data.success,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },

  virtualAccountCheckStatus: async (payload: { trxID: string }) => {
    try {
      const request = await BaseService.ci4Request().get<BaseResponseExternal>(
        "/tagihan/check-status",
        { params: payload },
      );
      return {
        success: request.data.status,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },

  virtualAccountCancel: async (id: string) => {
    try {
      const request = await BaseService.request().get<BillsResponse>(
        `/finance/virtual-accounts-cancel/${id}`,
      );
      return BaseService.response(request);
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },

  virtualAccountConfirm: async (id: string) => {
    try {
      const request = await BaseService.request().get<BillsResponse>(
        `/finance/virtual-accounts-confirm/${id}`,
      );
      return BaseService.response(request);
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },

  virtualAccountUploadBukti: async (payload: { trx_id: string; bukti_transaksi: File }) => {
    try {
      const form = new FormData();
      form.append("trx_id", payload.trx_id);
      form.append("bukti_transaksi", payload.bukti_transaksi);
      const request = await BaseService.request().post<BillsResponse>(
        `/finance/virtual-accounts-bukti`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return BaseService.response(request);
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },

  virtualAccountRequestZiswaf: async (payload: VirtualAccountRequestZiswaf) => {
    try {
      const request = await BaseService.ci4Request().post<BaseResponseExternal>(
        "/tagihan/submit_va_ziswaf",
        payload,
      );
      return {
        success: request.data.status,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },

  paymentSavingRequest: async (payload: PaymentSavingRequest) => {
    try {
      const request = await BaseService.ci4Request().post<
        BaseResponseExternal<{
          trxAmount: number;
          nis: string;
          customerName: string;
          virtualAccount: string;
          datetimeExpired: Date;
          trxID: string;
          jenis: string;
          requestPayment: string;
        }>
      >("/tagihan/submit_bayar_tagihan", payload);
      return {
        success: request.data.status,
        message: request.data.message,
        data: request.data.data,
      };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },

  pocket: async () => {
    try {
      const request =
        await BaseService.request().get<BillsResponse>("/finance/pocket");
      return BaseService.response(request);
    } catch (error: any) {
      return BaseService.response(error, true);
    }
  },
};
